/**
 * localization-form.js  (v6 — Bucks currency override on page load)
 *
 * Key fix vs v5:
 *  - On every page load, reads bucks_currency cookie and forces Bucks to use
 *    that currency BEFORE Bucks auto-detects from IP.
 *  - Uses multiple override strategies: Object.defineProperty intercept,
 *    early cookie/localStorage write, and post-load re-assertion.
 *  - This means: if user picks United States from India, USD stays on reload.
 *    If user picks India from US VPN, INR stays on reload.
 */

/* ─────────────────────────────────────────────
   STEP 1 — Run immediately (before anything else loads)
   Read the user-chosen currency from cookie and lock it in
   so Bucks cannot override it with IP-based detection.
   ───────────────────────────────────────────── */
(function lockBucksCurrencyEarly() {
  /* Read bucks_currency cookie */
  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|;)\\s*' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  const chosen = getCookie('bucks_currency') || getCookie('currency');
  if (!chosen) return; /* No user preference yet — let Bucks auto-detect normally */

  /* Re-write all storage keys immediately so Bucks reads our value */
  try {
    document.cookie = 'bucks_currency=' + chosen + '; path=/; max-age=31536000';
    document.cookie = 'currency='       + chosen + '; path=/; max-age=31536000';
  } catch(e) {}

  try {
    localStorage.setItem('bucks_currency',  chosen);
    localStorage.setItem('currency',        chosen);
    localStorage.setItem('nova_currency',   chosen);
    localStorage.setItem('mlveda_currency', chosen);
  } catch(e) {}

  /*
   * Intercept window.Bucks before Bucks script defines it.
   * When Bucks script runs and calls window.Bucks.init() or similar,
   * our defineProperty getter returns a proxy that forces the currency.
   */
  try {
    let _bucksReal = window.Bucks;
    Object.defineProperty(window, 'Bucks', {
      configurable: true,
      get: function() { return _bucksReal; },
      set: function(val) {
        _bucksReal = val;
        /* As soon as Bucks object is assigned, try to set currency */
        if (val && typeof val.setCurrency === 'function') {
          setTimeout(function() {
            try { val.setCurrency(chosen); } catch(e) {}
          }, 0);
        }
      }
    });
  } catch(e) {}

  try {
    let _bucksAppReal = window.BucksApp;
    Object.defineProperty(window, 'BucksApp', {
      configurable: true,
      get: function() { return _bucksAppReal; },
      set: function(val) {
        _bucksAppReal = val;
        if (val && typeof val.setCurrency === 'function') {
          setTimeout(function() {
            try { val.setCurrency(chosen); } catch(e) {}
          }, 0);
        }
      }
    });
  } catch(e) {}

  /*
   * Listen for any Bucks-related custom events fired during init
   * and immediately re-dispatch our chosen currency.
   */
  try {
    window.addEventListener('bucks:ready', function() {
      if (window.Bucks    && typeof window.Bucks.setCurrency    === 'function') window.Bucks.setCurrency(chosen);
      if (window.BucksApp && typeof window.BucksApp.setCurrency === 'function') window.BucksApp.setCurrency(chosen);
    });
    document.addEventListener('bucks:ready', function() {
      if (window.Bucks    && typeof window.Bucks.setCurrency    === 'function') window.Bucks.setCurrency(chosen);
      if (window.BucksApp && typeof window.BucksApp.setCurrency === 'function') window.BucksApp.setCurrency(chosen);
    });
  } catch(e) {}
})();


/* ─────────────────────────────────────────────
   Continent lookup table (ISO-2 → region name)
   ───────────────────────────────────────────── */
const CONTINENT_MAP = {
  DZ:'Africa',EG:'Africa',ET:'Africa',GH:'Africa',KE:'Africa',MA:'Africa',
  MZ:'Africa',NG:'Africa',SN:'Africa',ZA:'Africa',TZ:'Africa',TN:'Africa',
  UG:'Africa',CI:'Africa',CM:'Africa',AO:'Africa',BJ:'Africa',BW:'Africa',
  BF:'Africa',BI:'Africa',CV:'Africa',CF:'Africa',TD:'Africa',KM:'Africa',
  CD:'Africa',CG:'Africa',DJ:'Africa',GQ:'Africa',ER:'Africa',SZ:'Africa',
  GA:'Africa',GM:'Africa',GN:'Africa',GW:'Africa',LS:'Africa',LR:'Africa',
  LY:'Africa',MG:'Africa',MW:'Africa',ML:'Africa',MR:'Africa',MU:'Africa',
  NA:'Africa',NE:'Africa',RW:'Africa',ST:'Africa',SC:'Africa',SL:'Africa',
  SO:'Africa',SS:'Africa',SD:'Africa',TG:'Africa',ZM:'Africa',ZW:'Africa',
  RE:'Africa',YT:'Africa',
  AR:'Americas',BO:'Americas',BR:'Americas',CA:'Americas',CL:'Americas',
  CO:'Americas',CR:'Americas',CU:'Americas',DO:'Americas',EC:'Americas',
  SV:'Americas',GT:'Americas',HT:'Americas',HN:'Americas',JM:'Americas',
  MX:'Americas',NI:'Americas',PA:'Americas',PY:'Americas',PE:'Americas',
  PR:'Americas',TT:'Americas',US:'Americas',UY:'Americas',VE:'Americas',
  BB:'Americas',BZ:'Americas',GY:'Americas',SR:'Americas',
  AF:'Asia',AM:'Asia',AZ:'Asia',BH:'Asia',BD:'Asia',BT:'Asia',BN:'Asia',
  KH:'Asia',CN:'Asia',CY:'Asia',GE:'Asia',HK:'Asia',IN:'Asia',ID:'Asia',
  IR:'Asia',IQ:'Asia',IL:'Asia',JP:'Asia',JO:'Asia',KZ:'Asia',KW:'Asia',
  KG:'Asia',LA:'Asia',LB:'Asia',MO:'Asia',MY:'Asia',MV:'Asia',MN:'Asia',
  MM:'Asia',NP:'Asia',KP:'Asia',OM:'Asia',PK:'Asia',PS:'Asia',PH:'Asia',
  QA:'Asia',SA:'Asia',SG:'Asia',KR:'Asia',LK:'Asia',SY:'Asia',TW:'Asia',
  TJ:'Asia',TH:'Asia',TL:'Asia',TR:'Asia',TM:'Asia',AE:'Asia',UZ:'Asia',
  VN:'Asia',YE:'Asia',
  AL:'Europe',AD:'Europe',AT:'Europe',BY:'Europe',BE:'Europe',BA:'Europe',
  BG:'Europe',HR:'Europe',CZ:'Europe',DK:'Europe',EE:'Europe',FI:'Europe',
  FR:'Europe',DE:'Europe',GR:'Europe',HU:'Europe',IS:'Europe',IE:'Europe',
  IT:'Europe',XK:'Europe',LV:'Europe',LI:'Europe',LT:'Europe',LU:'Europe',
  MT:'Europe',MD:'Europe',MC:'Europe',ME:'Europe',NL:'Europe',MK:'Europe',
  NO:'Europe',PL:'Europe',PT:'Europe',RO:'Europe',RU:'Europe',SM:'Europe',
  RS:'Europe',SK:'Europe',SI:'Europe',ES:'Europe',SE:'Europe',CH:'Europe',
  UA:'Europe',GB:'Europe',VA:'Europe',AX:'Europe',GI:'Europe',
  AU:'Oceania',FJ:'Oceania',NZ:'Oceania',PG:'Oceania',WS:'Oceania',
  SB:'Oceania',TO:'Oceania',VU:'Oceania',CK:'Oceania',PF:'Oceania',
  GU:'Oceania',NC:'Oceania',
};

const REGION_ORDER = ['Africa','Americas','Asia','Europe','Oceania','Other'];

function getRegion(iso) { return CONTINENT_MAP[iso] || 'Other'; }

/* ─────────────────────────────────────────────
   Bucks currency sync
   Called when user PICKS a country in the modal.
   Sets all possible storage keys then submits form.
   ───────────────────────────────────────────── */
function setBucksCurrency(currencyCode) {
  if (!currencyCode) return;
  const exp = new Date(); exp.setFullYear(exp.getFullYear() + 1);

  /* Write ALL cookie keys — bucksCurrency (capital C) is what Bucks actually uses */
  ['bucksCurrency','bucks_currency','currency','cart_currency'].forEach(k => {
    document.cookie = k + '=' + currencyCode + '; path=/; expires=' + exp.toUTCString();
  });
  /* Write ALL localStorage keys */
  try {
    ['bucksCurrency','bucks_currency','currency','nova_currency',
     'mlveda_currency','user_chosen_currency'].forEach(k => {
      localStorage.setItem(k, currencyCode);
    });
  } catch(e) {}

  /* Update window.Shopify.currency.active — Bucks reads this */
  try {
    window.Shopify = window.Shopify || {};
    window.Shopify.currency = window.Shopify.currency || {};
    window.Shopify.currency.active = currencyCode;
  } catch(e) {}

  /* Call Bucks API directly */
  ['Bucks','BucksApp','bucks'].forEach(name => {
    try {
      const obj = window[name];
      if (obj && typeof obj.setCurrency === 'function') obj.setCurrency(currencyCode);
    } catch(e) {}
  });

  /* Fire all possible events */
  ['bucks:currency:change','currency:change','bucks:set:currency'].forEach(evName => {
    try {
      const ev = new CustomEvent(evName, { detail: { currency: currencyCode }, bubbles: true });
      document.dispatchEvent(ev); window.dispatchEvent(ev);
    } catch(e) {}
  });
}

/* ─────────────────────────────────────────────
   STEP 2 — Post-load re-assertion
   After DOM + scripts are fully loaded, hammer the
   chosen currency one more time to beat any lazy Bucks init.
   ───────────────────────────────────────────── */
(function reassertCurrencyAfterLoad() {
  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|;)\\s*' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function doReassert() {
    const chosen = getCookie('bucks_currency') || getCookie('currency');
    if (!chosen) return;
    /* Re-fire setCurrency calls in case Bucks finished loading after our early intercept */
    try {
      if (window.Bucks    && typeof window.Bucks.setCurrency    === 'function') window.Bucks.setCurrency(chosen);
      if (window.BucksApp && typeof window.BucksApp.setCurrency === 'function') window.BucksApp.setCurrency(chosen);
    } catch(e) {}
    /* Re-dispatch events */
    try {
      document.dispatchEvent(new CustomEvent('bucks:currency:change', { detail: { currency: chosen } }));
      window.dispatchEvent( new CustomEvent('bucks:currency:change',  { detail: { currency: chosen } }));
    } catch(e) {}
    /* Re-write localStorage in case Bucks cleared it */
    try {
      localStorage.setItem('bucks_currency',  chosen);
      localStorage.setItem('currency',        chosen);
      localStorage.setItem('nova_currency',   chosen);
      localStorage.setItem('mlveda_currency', chosen);
    } catch(e) {}
  }

  /* Fire at DOMContentLoaded, window load, and 1s/2s/4s after load */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', doReassert);
  } else {
    doReassert();
  }
  window.addEventListener('load', function() {
    doReassert();
    setTimeout(doReassert, 500);
    setTimeout(doReassert, 1000);
    setTimeout(doReassert, 2000);
    setTimeout(doReassert, 4000);
  });
})();


/* ─────────────────────────────────────────────
   Modal controller (singleton)
   ───────────────────────────────────────────── */
let _modalBuilt = false;

function buildModal() {
  if (_modalBuilt) return;
  _modalBuilt = true;

  const overlay = document.getElementById('country-modal-overlay');
  if (!overlay) return;

  /* Apply real currencies BEFORE grouping so data-country-currency is correct */
  if (typeof window.__applyRealCurrencies === 'function') {
    window.__applyRealCurrencies();
  }

  /* Group countries by continent */
  const rawItems = Array.from(overlay.querySelectorAll('#country-modal-list .country-modal__item'));
  const groups   = {};
  rawItems.forEach(btn => {
    const region = getRegion(btn.dataset.countryIso);
    if (!groups[region]) groups[region] = [];
    groups[region].push(btn);
  });

  const listEl = overlay.querySelector('#country-modal-list');
  listEl.innerHTML = '';

  REGION_ORDER.forEach(region => {
    if (!groups[region] || !groups[region].length) return;
    const section = document.createElement('div');
    section.className      = 'country-modal__region';
    section.dataset.region = region;

    const heading = document.createElement('h3');
    heading.className   = 'country-modal__region-title';
    heading.textContent = region;
    section.appendChild(heading);

    const grid = document.createElement('div');
    grid.className = 'country-modal__grid';
    groups[region].forEach(btn => grid.appendChild(btn));
    section.appendChild(grid);
    listEl.appendChild(section);
  });

  /* Search */
  const searchInput = overlay.querySelector('.country-modal__search');
  const noResults   = overlay.querySelector('.country-modal__no-results');

  searchInput.addEventListener('input', () => {
    const q = searchInput.value.trim().toLowerCase();
    let total = 0;
    overlay.querySelectorAll('.country-modal__item').forEach(btn => {
      // Search by country name OR currency code (e.g. "USD", "INR", "EUR")
      const nameMatch = (btn.dataset.countryName || '').toLowerCase().includes(q);
      const currencyMatch = (btn.dataset.countryCurrency || '').toLowerCase().includes(q);
      const match = !q || nameMatch || currencyMatch;
      btn.style.display = match ? '' : 'none';
      if (match) total++;
    });
    overlay.querySelectorAll('.country-modal__region').forEach(sec => {
      const anyVisible = Array.from(sec.querySelectorAll('.country-modal__item')).some(b => b.style.display !== 'none');
      sec.style.display = anyVisible ? '' : 'none';
    });
    noResults.classList.toggle('visible', total === 0);
  });

  /* Country click → sync Bucks then submit */
  overlay.addEventListener('click', e => {
    const btn = e.target.closest('.country-modal__item');
    if (!btn) return;
    submitLocalization(btn.dataset.countryIso, btn.dataset.countryCurrency);
  });

  /* Close button */
  overlay.querySelector('.country-modal__close').addEventListener('click', closeModal);

  /* Backdrop click (clicking outside modal content) */
  overlay.addEventListener('click', e => {
    if (e.target === overlay) closeModal();
  });

  /* Escape key */
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeModal(); });
}

function openModal() {
  buildModal();
  const overlay = document.getElementById('country-modal-overlay');
  if (!overlay) { console.warn('[localization] country-modal-overlay not found'); return; }
  /* Re-apply real currencies every time modal opens — ensures badges are always correct */
  if (typeof window.__applyRealCurrencies === 'function') {
    window.__applyRealCurrencies();
  }
  overlay.classList.add('is-open');
  document.body.classList.add('modal-open');
  const search = overlay.querySelector('.country-modal__search');
  if (search) { search.value = ''; search.dispatchEvent(new Event('input')); search.focus(); }
}

function closeModal() {
  const overlay = document.getElementById('country-modal-overlay');
  if (overlay) overlay.classList.remove('is-open');
  document.body.classList.remove('modal-open');
}

function submitLocalization(isoCode, currencyCode) {
  closeModal();

  /* 1. Persist to ALL storage keys immediately */
  if (currencyCode) setBucksCurrency(currencyCode);
  try {
    localStorage.setItem('user_country_manual',  isoCode + ':' + (currencyCode || ''));
    localStorage.setItem('user_chosen_currency', currencyCode || '');
    localStorage.removeItem('country_auto_set');
  } catch(e) {}

  /* 2. POST to Shopify localization to set the country cookie,
        but use return_to pointing to /localization/country?currency=XXX
        so Shopify itself redirects us to a URL Bucks will see. */
  const forms = Array.from(document.querySelectorAll('form.localization-form'));
  const form  = forms.find(f => f.querySelector('input[name="country_code"]')) || forms[0];

  if (form) {
    const hiddenInput = form.querySelector('input[name="country_code"]');
    if (hiddenInput) hiddenInput.value = isoCode;

    /* Build return_to: go back to current page WITH ?currency=XXX
       Bucks reads ?currency= on every page load and applies it immediately */
    try {
      const returnPath = window.location.pathname + (currencyCode ? '?currency=' + encodeURIComponent(currencyCode) : '');
      const actionUrl  = new URL(form.action || '/localization', window.location.origin);
      actionUrl.searchParams.set('return_to', returnPath);
      form.action = actionUrl.toString();
    } catch(e) {}

    form.submit();
  } else {
    /* Fallback: no form found — redirect directly with ?currency= in URL */
    const redirectUrl = window.location.pathname + (currencyCode ? '?currency=' + encodeURIComponent(currencyCode) : '');
    window.location.href = redirectUrl;
  }
}

/* ─────────────────────────────────────────────
   Stub <localization-form> custom element
   Keeps Shopify theme scripts happy; hides the
   legacy dropdown that the theme would normally show.
   Does NOT bind click here — the capture listener below handles that.
   ───────────────────────────────────────────── */
if (!customElements.get('localization-form')) {
  customElements.define('localization-form', class LocalizationForm extends HTMLElement {
    connectedCallback() {
      /* Hide legacy dropdown panels */
      this.querySelectorAll('.disclosure__list-wrapper').forEach(el => {
        el.hidden = true;
        el.style.display = 'none';
      });
    }
  });
}

/* ─────────────────────────────────────────────
   SINGLE capture-phase listener — the only place
   clicks are intercepted. Handles:
     • .country-modal-trigger  (our custom button)
     • .disclosure__button inside any localization column
     • .footer__localization area (bottom bar mobile)
   Using capture=true means this fires BEFORE the theme's
   own bubble-phase handlers, no race condition possible.
   ───────────────────────────────────────────── */
document.addEventListener('click', function(e) {
  /* 1. Our explicit trigger class — used in country-localization.liquid */
  if (e.target.closest('.country-modal-trigger')) {
    e.preventDefault();
    e.stopPropagation();
    openModal();
    return;
  }

  /* 2. Theme's disclosure button inside any localization wrapper */
  if (
    e.target.closest('.footer-localization-corporate-col .disclosure__button') ||
    e.target.closest('.footer-localization-corporate-col localization-form') ||
    e.target.closest('.footer__localization localization-form') ||
    e.target.closest('.footer__localization .disclosure__button')
  ) {
    e.preventDefault();
    e.stopPropagation();
    openModal();
    return;
  }
}, true /* capture phase */);

/* ─────────────────────────────────────────────
   MutationObserver — hide legacy dropdowns that
   get injected after initial render (deferred sections).
   ───────────────────────────────────────────── */
(function watchForNewPanels() {
  if (typeof MutationObserver === 'undefined') return;
  const observer = new MutationObserver(() => {
    document.querySelectorAll(
      '.footer-localization-corporate-col .disclosure__list-wrapper,' +
      '.footer__localization .disclosure__list-wrapper'
    ).forEach(el => {
      el.hidden = true;
      el.style.display = 'none';
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();

/* ─────────────────────────────────────────────
   Bucks widget — hide the floating widget since
   Bucks is positioned via Custom Position in footer.
   We don't need to move it; Bucks places itself at
   .bucks-embed which we added to the footer HTML.
   ───────────────────────────────────────────── */
function hideBucksFloating() {
  /* Hide ALL Bucks widget UI — we only want Bucks running in background for conversion */
  const hideSelectors = [
    'body > [class*="bucks"]',
    'body > [id*="bucks"]',
    '[id*="bucks-widget"]',
    '[class*="bucks-widget"]',
    '[id*="bucks-floating"]',
    '[class*="bucks-floating"]'
  ];
  hideSelectors.forEach(sel => {
    document.querySelectorAll(sel).forEach(el => {
      if (!el.closest('footer') && !el.closest('.footer')) {
        el.style.cssText = 'display:none!important';
      }
    });
  });

  /* Also hide everything INSIDE #footer-bucks-embed — Bucks injects its UI there
     but we want our custom country button to be the only visible UI */
  const embed = document.getElementById('footer-bucks-embed');
  if (embed) {
    embed.style.cssText = 'position:absolute!important;width:1px!important;height:1px!important;overflow:hidden!important;opacity:0!important;pointer-events:none!important;clip:rect(0,0,0,0)!important';
    /* Also hide children Bucks injects */
    embed.querySelectorAll('*').forEach(el => {
      el.style.cssText = 'visibility:hidden!important;pointer-events:none!important';
    });
  }
}

/* ─────────────────────────────────────────────
   Boot
   ───────────────────────────────────────────── */
function boot() {
  hideBucksFloating();
  /* Re-run after a short delay to catch lazy-loaded Bucks widget */
  setTimeout(hideBucksFloating, 1000);
  setTimeout(hideBucksFloating, 3000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}